import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { motion } from "framer-motion"
import {
	Card, Typography, Button, TextField, Box, Alert, CircularProgress, LinearProgress, Paper,
	Chip, Stepper, Step, StepLabel, IconButton, Snackbar,
	Dialog, DialogTitle, DialogContent, DialogActions, AppBar, Toolbar, Stack,
	Tabs, Tab, Divider, Collapse
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import {
	CameraAlt, DirectionsCar, Flag, CheckCircle, Warning,
	AttachMoney, Route, Close, Refresh, History, Visibility,
	ArrowBack, ExpandMore, ExpandLess, FilterList
} from "@mui/icons-material"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"
import useCamera from "../../../hooks/general/useCamera.jsx"
import TopoMenu from "../../../componentes/gerais/TopoMenu"
import MenuLateral from "../../../componentes/gerais/Menu"
import { indiceMenuRegistroKm } from "../../../componentes/gerais/menuPremiosDominio"
import { configAnim } from "../../../servicos/config/configAnim"

const styles = {
	container: {
		p: 2,
		maxWidth: 600,
		mx: "auto",
		pb: 4
	},
	stepperCard: {
		p: 2,
		mb: 3,
		borderRadius: 2,
		bgcolor: "#f8fafc"
	},
	configCard: {
		p: 2,
		mb: 3,
		borderRadius: 2,
		border: "1px solid #e2e8f0"
	},
	actionCard: {
		p: 3,
		mb: 3,
		borderRadius: 2,
		border: "2px solid"
	},
	actionCardIniciar: {
		borderColor: "#22c55e",
		bgcolor: "#f0fdf4"
	},
	actionCardFinalizar: {
		borderColor: "#3b82f6",
		bgcolor: "#eff6ff"
	},
	actionCardCorrecao: {
		borderColor: "#f59e0b",
		bgcolor: "#fffbeb"
	},
	actionTitle: {
		display: "flex",
		alignItems: "center",
		gap: 1,
		mb: 2,
		fontWeight: 600,
		fontSize: "1.1rem"
	},
	inputKm: {
		"& .MuiOutlinedInput-root": {
			fontSize: "1.2rem",
			fontWeight: 500
		}
	},
	fotoBox: {
		mt: 2,
		p: 2,
		bgcolor: "#f8fafc",
		borderRadius: 2,
		border: "1px dashed #cbd5e1"
	},
	fotoPreview: {
		width: "100%",
		maxHeight: 200,
		objectFit: "cover",
		borderRadius: 2,
		mt: 1
	},
	btnPrincipal: {
		mt: 2,
		py: 1.5,
		fontSize: "1rem",
		fontWeight: 600,
		borderRadius: 2
	},
	metricsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gap: 1.5,
		mb: 2
	},
	metricCard: {
		p: 2,
		borderRadius: 2,
		textAlign: "center"
	},
	metricIcon: {
		width: 40,
		height: 40,
		mb: 1
	},
	metricValue: {
		fontSize: "1.3rem",
		fontWeight: 700
	},
	metricLabel: {
		fontSize: "0.75rem",
		color: "#64748b"
	},
	jornadaItem: {
		p: 1.5,
		mb: 1,
		borderRadius: 1.5,
		bgcolor: "#f8fafc",
		border: "1px solid #e2e8f0"
	},
	fotoThumbWrap: {
		position: "relative",
		borderRadius: 1,
		overflow: "hidden",
		border: "1px solid",
		borderColor: "divider",
		bgcolor: "#f8fafc",
		cursor: "pointer",
		"&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 }
	},
	fotoThumbImg: {
		width: "100%",
		height: 160,
		objectFit: "cover",
		display: "block",
		verticalAlign: "top"
	}
}

const STATUS_CONFIG = {
	ABERTA: { label: "Em andamento", color: "info", icon: <DirectionsCar /> },
	NORMAL: { label: "Finalizada", color: "success", icon: <CheckCircle /> },
	DIVERGENTE: { label: "Divergente", color: "error", icon: <Warning /> },
	CORRECAO_SOLICITADA: { label: "Correção solicitada", color: "warning", icon: <Warning /> },
	CORRIGIDA: { label: "Corrigida", color: "success", icon: <CheckCircle /> },
	AJUSTADO: { label: "Ajustado", color: "default", icon: <CheckCircle /> }
}

const PERIODOS = [
	{ value: "mes_atual", label: "Mês atual" },
	{ value: "mes_anterior", label: "Mês anterior" },
	{ value: "personalizado", label: "Personalizado" }
]

function calcularPeriodo(tipo) {
	const hoje = new Date()
	if (tipo === "mes_atual") {
		const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
		const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
		return { dataInicio: fmtDate(ini), dataFim: fmtDate(fim) }
	}
	if (tipo === "mes_anterior") {
		const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
		const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0)
		return { dataInicio: fmtDate(ini), dataFim: fmtDate(fim) }
	}
	return { dataInicio: "", dataFim: "" }
}

function fmtDate(d) {
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, "0")
	const day = String(d.getDate()).padStart(2, "0")
	return `${y}-${m}-${day}`
}

function fmtMoeda(v) {
	const n = parseFloat(v) || 0
	return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

function KmFotoCamera({ onFinish, onError }) {
	useCamera(onFinish, onError)
	return (
		<Box sx={{ textAlign: "center", mt: 2 }}>
			<CircularProgress size={28} />
			<Typography variant="caption" display="block" sx={{ mt: 1 }}>Abrindo câmera…</Typography>
		</Box>
	)
}

function StatusBadge({ status }) {
	const cfg = STATUS_CONFIG[status] || { label: status, color: "default", icon: null }
	return (
		<Chip
			icon={cfg.icon}
			label={cfg.label}
			color={cfg.color}
			size="small"
			sx={{ fontWeight: 600 }}
		/>
	)
}

function StepperJornada({ temAberta, jornadaFinalizada }) {
	let activeStep = 0
	if (temAberta) activeStep = 1
	if (jornadaFinalizada) activeStep = 2

	return (
		<Paper sx={styles.stepperCard} elevation={0}>
			<Stepper activeStep={activeStep} alternativeLabel>
				<Step><StepLabel>Iniciar</StepLabel></Step>
				<Step><StepLabel>Em andamento</StepLabel></Step>
				<Step><StepLabel>Finalizado</StepLabel></Step>
			</Stepper>
		</Paper>
	)
}


function valorKmParaNumero(valor) {
	if (valor === null || valor === undefined || valor === "") return NaN
	if (typeof valor === "number" && Number.isFinite(valor)) return valor
	const raw = String(valor).trim().replace(/\s/g, "")
	if (!raw) return NaN
	if (raw.includes(",")) {
		return Number(raw.replace(/\./g, "").replace(",", "."))
	}
	if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
		return Number(raw.replace(/\./g, ""))
	}
	return Number(raw)
}

function fmtKmExibir(valor) {
	if (valor === null || valor === undefined || valor === "") return "—"
	const n = valorKmParaNumero(valor)
	if (!Number.isFinite(n)) return String(valor)
	return `${n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} km`
}

function parseDataHoraServidor(valor) {
	if (valor === null || valor === undefined || valor === "") return null
	const s = String(valor).trim()
	if (!s) return null
	const normalized = s.includes("T") ? s : s.replace(" ", "T")
	const d = new Date(normalized)
	return Number.isNaN(d.getTime()) ? null : d
}

function fmtDataHoraBr(valor) {
	const d = parseDataHoraServidor(valor)
	if (!d) return "—"
	return d.toLocaleString("pt-BR", {
		day: "2-digit", month: "2-digit", year: "numeric",
		hour: "2-digit", minute: "2-digit", hour12: false
	})
}

function fmtDataBr(valor) {
	const d = parseDataHoraServidor(valor)
	if (!d) return "—"
	return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function kmLeituraDiverge(strDigitado, ocrNum) {
	if (ocrNum === null || ocrNum === undefined || !Number.isFinite(Number(ocrNum))) return false
	const d = valorKmParaNumero(strDigitado)
	if (!Number.isFinite(d)) return false
	return Math.abs(d - Number(ocrNum)) > 0.01
}

function kmNumeroParaInputMascara(n) {
	if (!Number.isFinite(n)) return ""
	return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function formatarKm(valor) {
	if (valor === null || valor === undefined) return ""
	const raw = String(valor)
	const commaIdx = raw.indexOf(",")
	const hasComma = commaIdx !== -1
	let intStr, decStr
	if (hasComma) {
		intStr = raw.slice(0, commaIdx).replace(/\D/g, "")
		decStr = raw.slice(commaIdx + 1).replace(/\D/g, "").slice(0, 2)
	} else {
		intStr = raw.replace(/\D/g, "")
		decStr = ""
	}
	intStr = intStr.replace(/^0+(?=\d)/, "")
	if (intStr === "" && decStr === "") return hasComma ? "0," : ""
	if (intStr === "") intStr = "0"
	const intFmt = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
	return hasComma ? `${intFmt},${decStr}` : intFmt
}

function InputKm({ label, value, onChange, required, helperText }) {
	return (
		<TextField
			fullWidth
			margin="normal"
			label={label}
			value={value}
			onChange={e => onChange(formatarKm(e.target.value))}
			placeholder="ex.: 91308 ou 91.308,5"
			inputMode="decimal"
			required={required}
			helperText={helperText || "Somente leitura do hodômetro: dígitos; vírgula só se houver fração de km (ex.: 91.308,5)."}
			sx={{
				...styles.inputKm,
				"& .MuiOutlinedInput-input": { fontVariantNumeric: "tabular-nums" }
			}}
			InputProps={{
				endAdornment: (
					<Typography component="span" color="text.secondary" sx={{ ml: 1, fontWeight: 600, letterSpacing: 0.5 }}>
						km
					</Typography>
				)
			}}
		/>
	)
}

function HodometroFotoDetalhe({ url, titulo, labelAmpliar, onAmpliar }) {
	if (!url) return null
	return (
		<Box component="figure" sx={{ m: 0 }}>
			<Typography variant="caption" color="text.secondary" component="figcaption" sx={{ display: "block", mb: 1 }}>
				{titulo}
			</Typography>
			<Box
				component="button"
				type="button"
				onClick={() => onAmpliar({ url, label: labelAmpliar })}
				sx={{
					...styles.fotoThumbWrap,
					p: 0,
					width: "100%",
					font: "inherit",
					textAlign: "left",
					WebkitTapHighlightColor: "transparent"
				}}
				aria-label={`Ampliar foto: ${labelAmpliar}`}
			>
				<Box component="img" src={url} alt="" sx={styles.fotoThumbImg} loading="lazy" />
			</Box>
		</Box>
	)
}

function FotoHodometro({ url, onTirarFoto, onRemover, label, loading }) {
	return (
		<Box sx={styles.fotoBox}>
			<Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
				<CameraAlt fontSize="small" /> {label}
			</Typography>
			{url ? (
				<Box sx={{ position: "relative" }}>
					<img src={url} alt="Foto hodômetro" style={styles.fotoPreview} />
					<IconButton
						size="small"
						onClick={onRemover}
						sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.5)", color: "#fff" }}
					>
						<Close fontSize="small" />
					</IconButton>
				</Box>
			) : (
				<Button
					variant="outlined"
					startIcon={<CameraAlt />}
					onClick={onTirarFoto}
					disabled={loading}
					fullWidth
					sx={{ py: 2 }}
				>
					Tirar foto do hodômetro
				</Button>
			)}
		</Box>
	)
}

function emptyLeituraOdometro() {
	return { loading: false, km: null, confianca: "", raw: "", erro: "" }
}

function normalizarRespostaLeitura(r) {
	if (r?.resultado && r.dados) {
		const k = r.dados.kmAtual
		return {
			loading: false,
			km: k !== null && k !== undefined && k !== "" && Number.isFinite(Number(k)) ? Number(k) : null,
			confianca: r.dados.confianca || "",
			raw: r.dados.respostaBruta || "",
			erro: ""
		}
	}
	return { loading: false, km: null, confianca: "", raw: "", erro: r?.mensagem || "Não foi possível ler a foto." }
}

function PainelLeituraOcrOpenAi({ leitura, confirmacao, onConfirmarCorreto, onInformarOutro }) {
	if (leitura.loading) {
		return (
			<Box sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "#f8fafc", textAlign: "center" }}>
				<CircularProgress size={28} sx={{ mb: 1 }} />
				<Typography variant="body2" color="text.secondary">Analisando a foto do hodômetro…</Typography>
			</Box>
		)
	}
	if (leitura.erro) {
		return (
			<Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
				<Typography variant="body2" fontWeight={600}>Leitura automática</Typography>
				<Typography variant="body2" sx={{ mt: 0.5 }}>{leitura.erro}</Typography>
				<Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
					Informe o KM manualmente no campo abaixo.
				</Typography>
			</Alert>
		)
	}
	const km = leitura.km
	const temKm = km != null && Number.isFinite(Number(km))
	if (!temKm) {
		return (
			<Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
				<Typography variant="body2" fontWeight={600}>Leitura automática</Typography>
				<Typography variant="body2" sx={{ mt: 0.5 }}>
					Não foi possível identificar um KM numérico na foto. Informe o valor manualmente abaixo.
				</Typography>
			</Alert>
		)
	}
	if (confirmacao === "confirmado_ocr") {
		return (
			<Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
				<Typography variant="body2" fontWeight={600}>KM confirmado</Typography>
				<Typography variant="body2" sx={{ mt: 0.5 }}>
					Será registrado <strong>{fmtKmExibir(km)}</strong>
					{leitura.confianca ? ` (confiança: ${leitura.confianca})` : ""}.
				</Typography>
				<Button size="small" sx={{ mt: 1 }} onClick={onInformarOutro}>Corrigir para outro valor</Button>
			</Alert>
		)
	}
	if (confirmacao === "valor_manual") {
		return (
			<Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
				<Typography variant="body2">Você optou por informar o hodômetro manualmente.</Typography>
			</Alert>
		)
	}
	return (
		<Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "#fafafa" }}>
			<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Leitura na foto (automática)</Typography>
			<Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums", mb: 0.5 }}>{fmtKmExibir(km)}</Typography>
			{leitura.confianca ? (
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>Confiança: {leitura.confianca}</Typography>
			) : <Box sx={{ mb: 2 }} />}
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Confirme se este é o valor exibido no hodômetro. Se estiver errado, informe o correto — ao finalizar com divergência a jornada pode ficar como <strong>divergente</strong>.
			</Typography>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
				<Button variant="contained" color="success" onClick={onConfirmarCorreto}>Está correto</Button>
				<Button variant="outlined" color="warning" onClick={onInformarOutro}>Não, o hodômetro mostra outro valor</Button>
			</Stack>
		</Paper>
	)
}

// ─── Componente da aba Financeiro ───────────────────────────────────────────

function FiltrosPeriodo({ periodo, setPeriodo, dataInicio, setDataInicio, dataFim, setDataFim, onBuscar, carregando }) {
	return (
		<Box sx={{ mb: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
				<FilterList fontSize="small" color="action" />
				<Typography variant="subtitle2" color="text.secondary">Período</Typography>
			</Box>
			<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
				{PERIODOS.map(p => (
					<Chip
						key={p.value}
						label={p.label}
						onClick={() => setPeriodo(p.value)}
						variant={periodo === p.value ? "filled" : "outlined"}
						color={periodo === p.value ? "primary" : "default"}
						size="small"
					/>
				))}
			</Stack>
			{periodo === "personalizado" && (
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
					<TextField
						label="De"
						type="date"
						size="small"
						fullWidth
						value={dataInicio}
						onChange={e => setDataInicio(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="Até"
						type="date"
						size="small"
						fullWidth
						value={dataFim}
						onChange={e => setDataFim(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Stack>
			)}
			<Button
				variant="contained"
				size="small"
				onClick={onBuscar}
				disabled={carregando}
				startIcon={carregando ? <CircularProgress size={14} color="inherit" /> : null}
			>
				{carregando ? "Buscando..." : "Buscar"}
			</Button>
		</Box>
	)
}

function CardResumoFinanceiro({ totais }) {
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
			<Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Crédito recebido</Typography>
				<Typography variant="h6" fontWeight={700} color="#15803d">
					{fmtMoeda(totais.valorPago)}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{totais.qtdFechamentos} fechamento{totais.qtdFechamentos !== 1 ? "s" : ""}
				</Typography>
			</Paper>
			<Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Crédito a receber</Typography>
				<Typography variant="h6" fontWeight={700} color="#c2410c">
					{fmtMoeda(totais.valorPendente)}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{totais.qtdJornadasPendente} jornada{totais.qtdJornadasPendente !== 1 ? "s" : ""} sem fechamento
				</Typography>
			</Paper>
		</Box>
	)
}

function ItemFechamento({ fechamento }) {
	const [aberto, setAberto] = useState(false)
	return (
		<Box sx={{ mb: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
			<Box
				onClick={() => setAberto(a => !a)}
				sx={{
					display: "flex", justifyContent: "space-between", alignItems: "center",
					p: 1.5, bgcolor: "#f8fafc", cursor: "pointer",
					"&:hover": { bgcolor: "#f1f5f9" }
				}}
			>
				<Box>
					<Typography variant="body2" fontWeight={600} color="#15803d">
						{fmtMoeda(fechamento.valor_total)}
					</Typography>
					<Typography variant="caption" color="text.secondary" display="block">
						Fechado em {fmtDataBr(fechamento.criado_em)} · {fechamento.qtd_jornadas} jornada{fechamento.qtd_jornadas != 1 ? "s" : ""}
					</Typography>
				</Box>
				<IconButton size="small" aria-label={aberto ? "Recolher" : "Expandir"}>
					{aberto ? <ExpandLess /> : <ExpandMore />}
				</IconButton>
			</Box>
			<Collapse in={aberto}>
				<Box sx={{ p: 1.5, pt: 0.5, bgcolor: "#fff" }}>
					<Divider sx={{ mb: 1 }} />
					<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
						<Box>
							<Typography variant="caption" color="text.secondary">Total creditado</Typography>
							<Typography variant="body2" fontWeight={700}>{fmtMoeda(fechamento.valor_total)}</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">Jornadas incluídas</Typography>
							<Typography variant="body2" fontWeight={500}>{fechamento.qtd_jornadas}</Typography>
						</Box>
					</Box>

				</Box>
			</Collapse>
		</Box>
	)
}

function AbaFinanceiro({ dados, exibirFin }) {
	const [periodo, setPeriodo] = useState("mes_atual")
	const [dataInicio, setDataInicio] = useState("")
	const [dataFim, setDataFim] = useState("")
	const [carregando, setCarregando] = useState(false)
	const [financeiro, setFinanceiro] = useState(null)
	const [erro, setErro] = useState("")

	const buscar = useCallback(async (periodoOverride) => {
		setErro("")
		setCarregando(true)
		const p = periodoOverride ?? periodo
		let ini = "", fim = ""
		if (p !== "personalizado") {
			const calc = calcularPeriodo(p)
			ini = calc.dataInicio
			fim = calc.dataFim
		} else {
			ini = dataInicio
			fim = dataFim
		}
		const params = {}
		if (ini) params.dataInicio = ini
		if (fim) params.dataFim = fim
		const r = await ServicoAPI.kmFinanceiroProfissional(dados, params)
		setCarregando(false)
		if (r?.resultado) {
			setFinanceiro(r.dados)
		} else {
			setErro(r?.mensagem || "Não foi possível carregar o financeiro.")
			setFinanceiro(null)
		}
	}, [dados, periodo, dataInicio, dataFim])

	const handleSetPeriodo = useCallback((valor) => {
		setPeriodo(valor)
		if (valor !== "personalizado") {
			buscar(valor)
		}
	}, [buscar])

	const handleBuscar = useCallback(() => {
		buscar(periodo === "personalizado" ? undefined : periodo)
	}, [buscar, periodo])

	useEffect(() => {
		buscar()
	}, []) // eslint-disable-line

	if (!exibirFin) {
		return (
			<Alert severity="info" sx={{ borderRadius: 2, mt: 2 }}>
				O financeiro não está habilitado para o seu cadastro. Fale com o administrador.
			</Alert>
		)
	}

	return (
		<Box>
			<Card sx={{ ...styles.configCard, mt: 2 }} elevation={0}>
				<FiltrosPeriodo
					periodo={periodo}
					setPeriodo={handleSetPeriodo}
					dataInicio={dataInicio}
					setDataInicio={setDataInicio}
					dataFim={dataFim}
					setDataFim={setDataFim}
					onBuscar={handleBuscar}
					carregando={carregando}
				/>

				{erro && (
					<Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{erro}</Alert>
				)}

				{carregando && (
					<Box sx={{ mb: 2 }}>
						<LinearProgress sx={{ borderRadius: 1 }} />
					</Box>
				)}

				{financeiro && (
					<Box sx={{ opacity: carregando ? 0.45 : 1, transition: "opacity 0.2s" }}>
						<CardResumoFinanceiro totais={financeiro.totais} />

						{financeiro.fechamentos.length > 0 && (
							<>
								<Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
									<CheckCircle fontSize="small" color="success" /> Fechamentos ({financeiro.totais.qtdFechamentos})
								</Typography>
								{financeiro.fechamentos.map(f => (
									<ItemFechamento key={f.id} fechamento={f} />
								))}
							</>
						)}
					</Box>
				)}

				{!financeiro && !carregando && !erro && (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
						Selecione o período e clique em Buscar.
					</Typography>
				)}
			</Card>
		</Box>
	)
}

// ─── Componente principal ───────────────────────────────────────────────────

export default function RegistroJornadaKm() {
	const theme = useTheme()
	const fotoViewerFullScreen = useMediaQuery(theme.breakpoints.down("sm"))
	const { dados, centralAjuda } = useContext(ContextoGeral)
	const indiceMenuKm = indiceMenuRegistroKm(dados?.dominio, centralAjuda?.resultado === true)
	const [menu, setMenu] = useState(false)
	const [carregando, setCarregando] = useState(true)
	const [cfgRes, setCfgRes] = useState(null)
	const [jornada, setJornada] = useState(null)
	const [msg, setMsg] = useState({ text: "", type: "info" })
	const [kmInicial, setKmInicial] = useState("")
	const [kmFinal, setKmFinal] = useState("")
	const [urlInicio, setUrlInicio] = useState("")
	const [urlFim, setUrlFim] = useState("")
	const [obs, setObs] = useState("")
	const [salvando, setSalvando] = useState(false)
	const [camAberta, setCamAberta] = useState(false)
	const alvoFoto = useRef("inicio")
	const leituraInicioGen = useRef(0)
	const leituraFimGen = useRef(0)
	const [ultimasJornadas, setUltimasJornadas] = useState([])
	const [detalheJornada, setDetalheJornada] = useState(null)
	const [fotoAmpliada, setFotoAmpliada] = useState(null)
	const [leituraApiInicio, setLeituraApiInicio] = useState(() => emptyLeituraOdometro())
	const [leituraApiFim, setLeituraApiFim] = useState(() => emptyLeituraOdometro())
	const [confirmLeituraInicio, setConfirmLeituraInicio] = useState(null)
	const [confirmLeituraFim, setConfirmLeituraFim] = useState(null)
	const [divConfirm, setDivConfirm] = useState(null)
	const [abaAtiva, setAbaAtiva] = useState(0)
	const jornadaRef = useRef(jornada)
	jornadaRef.current = jornada

	const fecharDetalheJornada = useCallback(() => {
		setDetalheJornada(null)
		setFotoAmpliada(null)
	}, [])

	const ativo = !!cfgRes?.dados?.ativoModulo
	const cfg = cfgRes?.dados?.configuracao || {}
	const exibirFin = cfg.exibir_financeiro_profissional === "S"

	const recarregar = useCallback(async () => {
		if (!dados?.idProf || !dados?.dominio) return
		setCarregando(true)
		setMsg({ text: "", type: "info" })
		try {
			const r = await ServicoAPI.kmJornadaConfig(dados)
			setCfgRes(r)
			if (r?.resultado && r?.dados?.jornadaAberta && Object.keys(r.dados.jornadaAberta).length) {
				setJornada(r.dados.jornadaAberta)
			} else {
				setJornada(null)
			}
			let listaUltimas = []
			if (r?.resultado && r?.dados?.ativoModulo) {
				const rec = await ServicoAPI.kmJornadaRecentes(dados, { limite: 30 })
				if (rec?.resultado && Array.isArray(rec?.dados?.jornadas)) {
					listaUltimas = rec.dados.jornadas
				}
			}
			setUltimasJornadas(listaUltimas)
		} catch {
			setMsg({ text: "Falha ao carregar dados da jornada.", type: "error" })
		} finally {
			setCarregando(false)
		}
	}, [dados])

	useEffect(() => {
		recarregar()
	}, [recarregar])

	const kmOcrPreferidoInicio = useMemo(() => {
		if (leituraApiInicio.km != null && Number.isFinite(Number(leituraApiInicio.km))) return Number(leituraApiInicio.km)
		return null
	}, [leituraApiInicio])

	const kmOcrPreferidoFim = useMemo(() => {
		if (leituraApiFim.km != null && Number.isFinite(Number(leituraApiFim.km))) return Number(leituraApiFim.km)
		return null
	}, [leituraApiFim])

	useEffect(() => {
		const u = urlInicio.trim()
		if (jornadaRef.current?.status_jornada === "CORRECAO_SOLICITADA") {
			if (!u) { leituraInicioGen.current += 1; setLeituraApiInicio(emptyLeituraOdometro()); setConfirmLeituraInicio(null) }
			return
		}
		if (!u || !dados?.idProf) {
			leituraInicioGen.current += 1; setLeituraApiInicio(emptyLeituraOdometro()); setConfirmLeituraInicio(null)
			return
		}
		const gen = ++leituraInicioGen.current
		setConfirmLeituraInicio(null)
		setLeituraApiInicio({ ...emptyLeituraOdometro(), loading: true })
		let cancelled = false
		;(async () => {
			try {
				const r = await ServicoAPI.kmJornadaLerOdometro(dados, { urlFoto: u })
				if (cancelled || gen !== leituraInicioGen.current) return
				const norm = normalizarRespostaLeitura(r)
				setLeituraApiInicio(norm)
				setConfirmLeituraInicio(norm.erro || norm.km == null || !Number.isFinite(Number(norm.km)) ? "valor_manual" : "pendente")
			} catch {
				if (cancelled || gen !== leituraInicioGen.current) return
				setLeituraApiInicio({ ...emptyLeituraOdometro(), erro: "Falha na análise automática da foto." })
				setConfirmLeituraInicio("valor_manual")
			}
		})()
		return () => { cancelled = true }
	}, [urlInicio, dados])

	useEffect(() => {
		const u = urlFim.trim()
		if (jornadaRef.current?.status_jornada === "CORRECAO_SOLICITADA") {
			if (!u) { leituraFimGen.current += 1; setLeituraApiFim(emptyLeituraOdometro()); setConfirmLeituraFim(null) }
			return
		}
		if (!u || !dados?.idProf) {
			leituraFimGen.current += 1; setLeituraApiFim(emptyLeituraOdometro()); setConfirmLeituraFim(null)
			return
		}
		const gen = ++leituraFimGen.current
		setConfirmLeituraFim(null)
		setLeituraApiFim({ ...emptyLeituraOdometro(), loading: true })
		let cancelled = false
		;(async () => {
			try {
				const r = await ServicoAPI.kmJornadaLerOdometro(dados, { urlFoto: u })
				if (cancelled || gen !== leituraFimGen.current) return
				const norm = normalizarRespostaLeitura(r)
				setLeituraApiFim(norm)
				setConfirmLeituraFim(norm.erro || norm.km == null || !Number.isFinite(Number(norm.km)) ? "valor_manual" : "pendente")
			} catch {
				if (cancelled || gen !== leituraFimGen.current) return
				setLeituraApiFim({ ...emptyLeituraOdometro(), erro: "Falha na análise automática da foto." })
				setConfirmLeituraFim("valor_manual")
			}
		})()
		return () => { cancelled = true }
	}, [urlFim, dados])

	const kmDigitadoValido = useCallback((s) => {
		const n = valorKmParaNumero(s)
		return Number.isFinite(n) && n >= 0
	}, [])

	const podeIniciarJornada = useMemo(() => {
		if (!urlInicio.trim() || !dados?.idProf) return false
		if (leituraApiInicio.loading) return false
		if (confirmLeituraInicio === "pendente" || confirmLeituraInicio === null) return false
		if (confirmLeituraInicio !== "confirmado_ocr" && confirmLeituraInicio !== "valor_manual") return false
		return kmDigitadoValido(kmInicial)
	}, [urlInicio, dados, leituraApiInicio.loading, confirmLeituraInicio, kmInicial, kmDigitadoValido])

	const podeFinalizarJornada = useMemo(() => {
		if (!urlFim.trim() || !dados?.idProf) return false
		if (leituraApiFim.loading) return false
		if (confirmLeituraFim === "pendente" || confirmLeituraFim === null) return false
		if (confirmLeituraFim !== "confirmado_ocr" && confirmLeituraFim !== "valor_manual") return false
		return kmDigitadoValido(kmFinal)
	}, [urlFim, dados, leituraApiFim.loading, confirmLeituraFim, kmFinal, kmDigitadoValido])

	const onFotoOk = (url) => {
		if (alvoFoto.current === "inicio") setUrlInicio(url)
		else setUrlFim(url)
		setCamAberta(false)
	}
	const onFotoErro = (e) => {
		setMsg({ text: typeof e === "string" ? e : "Erro ao enviar foto", type: "error" })
		setCamAberta(false)
	}
	const abrirCam = (alvo) => { alvoFoto.current = alvo; setCamAberta(true) }
	const parseKm = (v) => v.replace(/\./g, "").replace(",", ".")

	const iniciar = async (opts = {}) => {
		const ignorarDiv = opts.ignorarDivergencia === true
		if (!urlInicio.trim()) { setMsg({ text: "Tire primeiro a foto do hodômetro (início).", type: "warning" }); return }
		if (leituraApiInicio.loading) { setMsg({ text: "Aguarde a análise automática da foto.", type: "warning" }); return }
		if (confirmLeituraInicio === "pendente") { setMsg({ text: "Confirme se a leitura na foto está correta.", type: "warning" }); return }
		if (!kmDigitadoValido(kmInicial)) { setMsg({ text: "Informe o KM inicial do hodômetro (válido).", type: "warning" }); return }
		if (!ignorarDiv && kmLeituraDiverge(kmInicial, kmOcrPreferidoInicio)) {
			setDivConfirm({ acao: "inicio", kmDig: kmInicial, kmOcr: kmOcrPreferidoInicio }); return
		}
		setSalvando(true); setMsg({ text: "", type: "info" })
		const body = { kmInicial: parseKm(kmInicial), urlFotoInicio: urlInicio.trim(), observacao: obs }
		if (kmOcrPreferidoInicio != null && Number.isFinite(kmOcrPreferidoInicio)) body.kmInicialOcr = String(kmOcrPreferidoInicio)
		const metaOcr = { fonte: "openai", api: { km: leituraApiInicio.km, confianca: leituraApiInicio.confianca, respostaBruta: leituraApiInicio.raw, erro: leituraApiInicio.erro || undefined }, confirmacaoProfissional: confirmLeituraInicio }
		if (metaOcr.api.confianca || metaOcr.api.respostaBruta || metaOcr.api.erro || confirmLeituraInicio) body.textoOcrInicio = JSON.stringify(metaOcr)
		const r = await ServicoAPI.kmJornadaIniciar(dados, body)
		setSalvando(false)
		if (r?.resultado) {
			setMsg({ text: r.mensagem || "Jornada iniciada!", type: "success" })
			setKmInicial(""); setUrlInicio(""); setObs(""); setConfirmLeituraInicio(null); setLeituraApiInicio(emptyLeituraOdometro()); setDivConfirm(null)
			recarregar()
		} else {
			setMsg({ text: r?.mensagem || "Não foi possível iniciar.", type: "error" })
		}
	}

	const finalizar = async (opts = {}) => {
		const ignorarDiv = opts.ignorarDivergencia === true
		if (!urlFim.trim()) { setMsg({ text: "Tire primeiro a foto do hodômetro (final).", type: "warning" }); return }
		if (leituraApiFim.loading) { setMsg({ text: "Aguarde a análise automática da foto.", type: "warning" }); return }
		if (confirmLeituraFim === "pendente") { setMsg({ text: "Confirme se a leitura na foto está correta.", type: "warning" }); return }
		if (!kmDigitadoValido(kmFinal)) { setMsg({ text: "Informe o KM final do hodômetro (válido).", type: "warning" }); return }
		if (!ignorarDiv && kmLeituraDiverge(kmFinal, kmOcrPreferidoFim)) {
			setDivConfirm({ acao: "fim", kmDig: kmFinal, kmOcr: kmOcrPreferidoFim }); return
		}
		setSalvando(true); setMsg({ text: "", type: "info" })
		const body = { kmFinal: parseKm(kmFinal), urlFotoFim: urlFim.trim() }
		if (kmOcrPreferidoFim != null && Number.isFinite(kmOcrPreferidoFim)) body.kmFinalOcr = String(kmOcrPreferidoFim)
		const metaOcrF = { fonte: "openai", api: { km: leituraApiFim.km, confianca: leituraApiFim.confianca, respostaBruta: leituraApiFim.raw, erro: leituraApiFim.erro || undefined }, confirmacaoProfissional: confirmLeituraFim }
		if (metaOcrF.api.confianca || metaOcrF.api.respostaBruta || metaOcrF.api.erro || confirmLeituraFim) body.textoOcrFim = JSON.stringify(metaOcrF)
		const r = await ServicoAPI.kmJornadaFinalizar(dados, body)
		setSalvando(false)
		if (r?.resultado) {
			setMsg({ text: r.mensagem || "Jornada finalizada!", type: "success" })
			setKmFinal(""); setUrlFim(""); setConfirmLeituraFim(null); setLeituraApiFim(emptyLeituraOdometro()); setDivConfirm(null)
			recarregar()
		} else {
			setMsg({ text: r?.mensagem || "Não foi possível finalizar.", type: "error" })
		}
	}

	const responderCorrecao = async () => {
		if (!jornada?.id) return
		setSalvando(true); setMsg({ text: "", type: "info" })
		const body = { idJornada: jornada.id, observacao: obs }
		if (kmInicial) body.kmInicial = parseKm(kmInicial)
		if (kmFinal) body.kmFinal = parseKm(kmFinal)
		if (urlInicio.trim()) body.urlFotoInicio = urlInicio.trim()
		if (urlFim.trim()) body.urlFotoFim = urlFim.trim()
		const r = await ServicoAPI.kmJornadaResponderCorrecao(dados, body)
		setSalvando(false)
		if (r?.resultado) {
			setMsg({ text: r.mensagem || "Correção enviada!", type: "success" })
			setKmInicial(""); setKmFinal(""); setUrlInicio(""); setUrlFim("")
			setConfirmLeituraInicio(null); setConfirmLeituraFim(null); setObs("")
			recarregar()
		} else {
			setMsg({ text: r?.mensagem || "Não foi possível enviar correção.", type: "error" })
		}
	}

	if (carregando) {
		return (
			<motion.div className="telaAjustada pb-0" initial={configAnim.initial} animate={configAnim.animate} exit={configAnim.exit} transition={configAnim.transition}>
				<TopoMenu voltar="/app/home" titulo="REGISTRO JORNADA" setShow={setMenu} />
				<MenuLateral show={menu} setShow={setMenu} ativo={indiceMenuKm} />
				<Box sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
					<CircularProgress size={40} />
					<Typography color="text.secondary">Carregando...</Typography>
				</Box>
			</motion.div>
		)
	}

	if (!ativo) {
		return (
			<motion.div className="telaAjustada pb-0" initial={configAnim.initial} animate={configAnim.animate} exit={configAnim.exit} transition={configAnim.transition}>
				<TopoMenu voltar="/app/home" titulo="REGISTRO JORNADA" setShow={setMenu} />
				<MenuLateral show={menu} setShow={setMenu} ativo={indiceMenuKm} />
				<Box sx={styles.container}>
					<Alert severity="info" sx={{ borderRadius: 2 }}>
						Este recurso não está ativo para o seu cadastro. Entre em contato com o administrador.
					</Alert>
				</Box>
			</motion.div>
		)
	}

	const status = jornada?.status_jornada || ""
	const emCorrecao = status === "CORRECAO_SOLICITADA"
	const temAberta = !!jornada && !jornada.data_hora_fim
	const jornadaFinalizada = !!jornada?.data_hora_fim

	return (
		<motion.div
			className="telaAjustada pb-0"
			initial={configAnim.initial}
			animate={configAnim.animate}
			exit={configAnim.exit}
			transition={configAnim.transition}
		>
			<TopoMenu voltar="/app/home" titulo="REGISTRO JORNADA" setShow={setMenu} />
			<MenuLateral show={menu} setShow={setMenu} ativo={indiceMenuKm} />
			<Snackbar
				open={Boolean(msg.text)}
				autoHideDuration={msg.type === "error" ? 9000 : msg.type === "warning" ? 7000 : 5000}
				onClose={() => setMsg({ text: "", type: "info" })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ bottom: { xs: "max(16px, env(safe-area-inset-bottom))", sm: 24 } }}
			>
				<Alert onClose={() => setMsg({ text: "", type: "info" })} severity={msg.type} variant="filled" sx={{ width: "100%", maxWidth: "min(calc(100vw - 32px), 480px)" }}>
					{msg.text}
				</Alert>
			</Snackbar>

			<Box sx={styles.container}>
				{/* Abas */}
				<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
					<Tabs value={abaAtiva} onChange={(_, v) => setAbaAtiva(v)} variant="fullWidth">
						<Tab
							label="Jornadas"
							icon={<Route fontSize="small" />}
							iconPosition="start"
							sx={{ minHeight: 48, fontWeight: 600 }}
						/>
						<Tab
							label="Financeiro"
							icon={<AttachMoney fontSize="small" />}
							iconPosition="start"
							sx={{ minHeight: 48, fontWeight: 600 }}
							disabled={!exibirFin}
						/>
					</Tabs>
				</Box>

				{/* ─── Aba Jornadas ─── */}
				{abaAtiva === 0 && (
					<>
						<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
							<IconButton onClick={recarregar} size="small" aria-label="Atualizar dados" color="primary">
								<Refresh />
							</IconButton>
						</Box>

						<StepperJornada temAberta={temAberta} jornadaFinalizada={jornadaFinalizada} />

						<Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
							Envie a foto do hodômetro: o sistema <strong>analisa automaticamente</strong> (OpenAI) e mostra o KM encontrado.
							Confirme se bate com o painel do veículo — se estiver certo, não precisa digitar; se estiver errado, informe o valor correto.
							Se divergir da leitura da foto, a jornada pode ficar como <strong>divergente</strong> para o painel validar.
						</Typography>

						{temAberta && (
							<Card sx={{ ...styles.actionCard, ...(emCorrecao ? styles.actionCardCorrecao : styles.actionCardFinalizar) }} elevation={0}>
								<Typography sx={{ ...styles.actionTitle, color: emCorrecao ? "#d97706" : "#2563eb" }}>
									{emCorrecao ? <Warning /> : <Flag />}
									{emCorrecao ? "Correção solicitada" : "Jornada em andamento"}
								</Typography>
								<Box sx={{ mb: 2 }}><StatusBadge status={status} /></Box>
								<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
									<Box sx={{ bgcolor: "#fff", p: 1.5, borderRadius: 1.5 }}>
										<Typography variant="caption" color="text.secondary">Início</Typography>
										<Typography variant="body2" fontWeight={500}>{fmtDataHoraBr(jornada.data_hora_inicio)}</Typography>
									</Box>
									<Box sx={{ bgcolor: "#fff", p: 1.5, borderRadius: 1.5, minWidth: 0 }}>
										<Typography variant="caption" color="text.secondary">KM inicial</Typography>
										<Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
											{fmtKmExibir(jornada.km_inicial_atual)}
										</Typography>
									</Box>
								</Box>
								{emCorrecao && (
									<Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
										O administrador solicitou correção nos dados. Ajuste os valores abaixo e envie novamente.
									</Alert>
								)}
								{!emCorrecao && (
									<>
										<FotoHodometro url={urlFim} onTirarFoto={() => abrirCam("fim")} onRemover={() => { setUrlFim(""); setKmFinal(""); setConfirmLeituraFim(null); setLeituraApiFim(emptyLeituraOdometro()) }} label="Foto do hodômetro (final) — envie antes do KM" loading={salvando} />
										{camAberta && alvoFoto.current === "fim" && <KmFotoCamera onFinish={onFotoOk} onError={onFotoErro} />}
										{urlFim.trim() ? (
											<PainelLeituraOcrOpenAi
												leitura={leituraApiFim}
												confirmacao={confirmLeituraFim}
												onConfirmarCorreto={() => { const k = leituraApiFim.km; if (k != null && Number.isFinite(Number(k))) { setKmFinal(kmNumeroParaInputMascara(Number(k))); setConfirmLeituraFim("confirmado_ocr") } }}
												onInformarOutro={() => { setKmFinal(""); setConfirmLeituraFim("valor_manual") }}
											/>
										) : null}
										{confirmLeituraFim === "valor_manual" ? (
											<InputKm label="KM final (hodômetro)" value={kmFinal} onChange={setKmFinal} required helperText="Digite o valor exibido no hodômetro." />
										) : confirmLeituraFim === "confirmado_ocr" ? null : urlFim.trim() ? (
											<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Confirme a leitura ou informe outro valor.</Typography>
										) : null}
										<Button variant="contained" color="primary" fullWidth disabled={salvando || !podeFinalizarJornada} onClick={() => finalizar()} startIcon={<Flag />} sx={styles.btnPrincipal}>
											{salvando ? "Finalizando..." : "Finalizar jornada"}
										</Button>
									</>
								)}
								{emCorrecao && (
									<>
										<InputKm label="KM inicial corrigido" value={kmInicial} onChange={setKmInicial} />
										<InputKm label="KM final corrigido" value={kmFinal} onChange={setKmFinal} />
										<FotoHodometro url={urlInicio} onTirarFoto={() => abrirCam("inicio")} onRemover={() => { setUrlInicio(""); setKmInicial(""); setConfirmLeituraInicio(null) }} label="Foto início (opcional)" loading={salvando} />
										<FotoHodometro url={urlFim} onTirarFoto={() => abrirCam("fim")} onRemover={() => { setUrlFim(""); setKmFinal(""); setConfirmLeituraFim(null) }} label="Foto fim (opcional)" loading={salvando} />
										{camAberta && <KmFotoCamera onFinish={onFotoOk} onError={onFotoErro} />}
										<TextField fullWidth margin="normal" label="Observação" value={obs} onChange={e => setObs(e.target.value)} multiline rows={2} />
										<Button variant="contained" color="warning" fullWidth disabled={salvando} onClick={responderCorrecao} sx={styles.btnPrincipal}>
											{salvando ? "Enviando..." : "Enviar correção"}
										</Button>
									</>
								)}
							</Card>
						)}

						{!temAberta && (
							<Card sx={{ ...styles.actionCard, ...styles.actionCardIniciar }} elevation={0}>
								<Typography sx={{ ...styles.actionTitle, color: "#16a34a" }}>
									<DirectionsCar /> Iniciar nova jornada
								</Typography>
								<FotoHodometro url={urlInicio} onTirarFoto={() => abrirCam("inicio")} onRemover={() => { setUrlInicio(""); setKmInicial(""); setConfirmLeituraInicio(null); setLeituraApiInicio(emptyLeituraOdometro()) }} label="Foto do hodômetro (início) — envie antes do KM" loading={salvando} />
								{camAberta && alvoFoto.current === "inicio" && <KmFotoCamera onFinish={onFotoOk} onError={onFotoErro} />}
								{urlInicio.trim() ? (
									<PainelLeituraOcrOpenAi
										leitura={leituraApiInicio}
										confirmacao={confirmLeituraInicio}
										onConfirmarCorreto={() => { const k = leituraApiInicio.km; if (k != null && Number.isFinite(Number(k))) { setKmInicial(kmNumeroParaInputMascara(Number(k))); setConfirmLeituraInicio("confirmado_ocr") } }}
										onInformarOutro={() => { setKmInicial(""); setConfirmLeituraInicio("valor_manual") }}
									/>
								) : null}
								{confirmLeituraInicio === "valor_manual" ? (
									<InputKm label="KM inicial (hodômetro)" value={kmInicial} onChange={setKmInicial} required helperText="Digite o valor exibido no hodômetro." />
								) : confirmLeituraInicio === "confirmado_ocr" ? null : urlInicio.trim() ? (
									<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Confirme a leitura ou informe outro valor.</Typography>
								) : null}
								<TextField fullWidth margin="normal" label="Observação (opcional)" value={obs} onChange={e => setObs(e.target.value)} placeholder="Ex.: início do turno da manhã" multiline rows={2} />
								<Button variant="contained" color="success" fullWidth disabled={salvando || !podeIniciarJornada} onClick={() => iniciar()} startIcon={<DirectionsCar />} sx={styles.btnPrincipal}>
									{salvando ? "Iniciando..." : "Iniciar jornada"}
								</Button>
							</Card>
						)}

						<Card sx={{ ...styles.configCard, mt: 3 }} elevation={0}>
							<Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
								<History fontSize="small" /> Últimas jornadas (até 30)
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
								Resumo operacional: datas, km rodado e situação.
							</Typography>
							{ultimasJornadas.length === 0 ? (
								<Typography variant="body2" color="text.secondary">Nenhuma jornada encontrada ainda.</Typography>
							) : (
								<Box sx={{ maxHeight: 480, overflow: "auto", pr: 0.5 }}>
									{ultimasJornadas.map(j => (
										<Box key={j.id} sx={styles.jornadaItem}>
											<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="body2" fontWeight={500}>{fmtDataHoraBr(j.data_hora_inicio)}</Typography>
													<Typography variant="caption" color="text.secondary" display="block">{fmtKmExibir(j.km_rodado)} rodados</Typography>
													<Box sx={{ mt: 0.5 }}><StatusBadge status={j.status_jornada} /></Box>
												</Box>
												<Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => setDetalheJornada(j)}>
													Detalhes
												</Button>
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Card>
					</>
				)}

				{/* ─── Aba Financeiro ─── */}
				<Box sx={{ display: abaAtiva === 1 ? "block" : "none" }}>
					<AbaFinanceiro dados={dados} exibirFin={exibirFin} />
				</Box>
			</Box>

			{/* Modal detalhe jornada */}
			<Dialog open={Boolean(detalheJornada)} onClose={fecharDetalheJornada} fullWidth maxWidth="sm">
				<DialogTitle sx={{ fontWeight: 700, pr: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
					<span>Detalhes da jornada</span>
					<IconButton aria-label="Fechar" onClick={fecharDetalheJornada} size="small"><Close /></IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{detalheJornada && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<Box>
								<Typography variant="caption" color="text.secondary">Situação</Typography>
								<Box sx={{ mt: 0.5 }}><StatusBadge status={detalheJornada.status_jornada} /></Box>
							</Box>
							<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
								<Box>
									<Typography variant="caption" color="text.secondary">Início</Typography>
									<Typography variant="body2" fontWeight={500}>{fmtDataHoraBr(detalheJornada?.data_hora_inicio)}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Fim</Typography>
									<Typography variant="body2" fontWeight={500}>{detalheJornada.data_hora_fim ? fmtDataHoraBr(detalheJornada.data_hora_fim) : "—"}</Typography>
								</Box>
							</Box>
							<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
								<Box>
									<Typography variant="caption" color="text.secondary">KM inicial</Typography>
									<Typography variant="body2">{fmtKmExibir(detalheJornada.km_inicial_atual)}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">KM final</Typography>
									<Typography variant="body2">{fmtKmExibir(detalheJornada.km_final_atual)}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">KM rodado</Typography>
									<Typography variant="body2" fontWeight={600}>{fmtKmExibir(detalheJornada.km_rodado)}</Typography>
								</Box>
							</Box>
							{detalheJornada.id_solicitante ? (
								<Box>
									<Typography variant="caption" color="text.secondary">Cliente (código)</Typography>
									<Typography variant="body2">#{detalheJornada.id_solicitante}</Typography>
								</Box>
							) : null}
							{detalheJornada.observacao_profissional ? (
								<Box>
									<Typography variant="caption" color="text.secondary">Sua observação</Typography>
									<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{detalheJornada.observacao_profissional}</Typography>
								</Box>
							) : null}
							{detalheJornada.justificativa_ajuste ? (
								<Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
									<Typography variant="caption" color="text.secondary">Motivo do ajuste de KM</Typography>
									<Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", fontWeight: 500 }}>{detalheJornada.justificativa_ajuste}</Typography>
									{detalheJornada.data_hora_ajuste ? (
										<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
											Ajustado em {fmtDataHoraBr(detalheJornada.data_hora_ajuste)}
											{detalheJornada.tipo_usuario_ajuste ? ` por ${String(detalheJornada.tipo_usuario_ajuste).toLowerCase()}` : ""}
										</Typography>
									) : null}
								</Box>
							) : null}
							{(detalheJornada.url_foto_inicio || detalheJornada.url_foto_fim) ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									<Box>
										<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Fotos do hodômetro</Typography>
										<Typography variant="caption" color="text.secondary">Toque na miniatura para ver em tela cheia.</Typography>
									</Box>
									{detalheJornada.url_foto_inicio ? (
										<HodometroFotoDetalhe url={detalheJornada.url_foto_inicio} titulo="Hodômetro — início" labelAmpliar="Foto do hodômetro — início" onAmpliar={setFotoAmpliada} />
									) : <Typography variant="body2" color="text.secondary">Sem foto de início.</Typography>}
									{detalheJornada.url_foto_fim ? (
										<HodometroFotoDetalhe url={detalheJornada.url_foto_fim} titulo="Hodômetro — fim" labelAmpliar="Foto do hodômetro — fim" onAmpliar={setFotoAmpliada} />
									) : <Typography variant="body2" color="text.secondary">Sem foto de fim.</Typography>}
								</Box>
							) : null}
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={fecharDetalheJornada}>Fechar</Button>
				</DialogActions>
			</Dialog>

			{/* Modal divergência */}
			<Dialog open={Boolean(divConfirm)} onClose={() => setDivConfirm(null)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>Divergência na leitura</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" sx={{ lineHeight: 1.6 }}>
						O KM que você informou (<strong>{fmtKmExibir(valorKmParaNumero(divConfirm?.kmDig))}</strong>) é diferente
						do que foi identificado na foto (<strong>{fmtKmExibir(divConfirm?.kmOcr)}</strong>).
					</Typography>
					<Typography variant="body2" sx={{ mt: 2, lineHeight: 1.6 }}>
						Se o valor no hodômetro estiver correto, confirme para registrar. A jornada ficará como{" "}
						<strong>divergente</strong> para validação no painel.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
					<Button onClick={() => setDivConfirm(null)} color="inherit">Voltar e ajustar</Button>
					<Button variant="contained" color="warning" onClick={() => {
						const ac = divConfirm?.acao; setDivConfirm(null)
						if (ac === "inicio") iniciar({ ignorarDivergencia: true })
						else if (ac === "fim") finalizar({ ignorarDivergencia: true })
					}}>
						Confirmar e registrar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Viewer foto fullscreen */}
			<Dialog
				open={Boolean(fotoAmpliada)}
				onClose={() => setFotoAmpliada(null)}
				fullScreen={fotoViewerFullScreen}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { bgcolor: "#0b1220", color: "#f8fafc", ...(fotoViewerFullScreen ? {} : { maxHeight: "90vh" }) } }}
			>
				<AppBar sx={{ position: "relative", bgcolor: "rgba(15,23,42,0.95)" }} elevation={0} color="default">
					<Toolbar variant="dense" sx={{ gap: 1, minHeight: { xs: 48, sm: 52 } }}>
						<IconButton edge="start" color="inherit" onClick={() => setFotoAmpliada(null)} aria-label="Voltar"><ArrowBack /></IconButton>
						<Typography variant="subtitle1" component="div" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{fotoAmpliada?.label || "Foto"}
						</Typography>
					</Toolbar>
				</AppBar>
				<DialogContent sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: fotoViewerFullScreen ? "calc(100vh - 48px)" : 360, bgcolor: "#000" }}>
					{fotoAmpliada?.url ? (
						<Box component="img" src={fotoAmpliada.url} alt="" sx={{ maxWidth: "100%", maxHeight: fotoViewerFullScreen ? "calc(100vh - 56px)" : "min(70vh, 640px)", objectFit: "contain" }} />
					) : null}
				</DialogContent>
			</Dialog>
		</motion.div>
	)
}
